let vapidPublicKey = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';

/** Manuel enable loading toast — success/error aynı id ile üzerine yazılır */
export const PUSH_ENABLE_TOAST_ID = 'push-enable-loading';
export const PUSH_LOADING_MAX_MS = 12000;

let pushInProgress = null;

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

const isAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
};

const isChrome = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return ua.includes('Chrome') && !ua.includes('Edg');
};

const needsStandaloneIOS = () => {
  if (typeof window === 'undefined') return false;
  return isIOS() && !window.matchMedia('(display-mode: standalone)').matches;
};

export const setVapidPublicKey = (key) => {
  vapidPublicKey = key || '';
};

export const getPushSupportStatus = () => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return { supported: false, reason: 'unknown' };
    }

    if (!('Notification' in window)) {
      return { supported: false, reason: 'notification' };
    }

    if (needsStandaloneIOS()) {
      return { supported: false, reason: 'iosStandalone' };
    }

    if (!('serviceWorker' in navigator)) {
      return { supported: false, reason: 'serviceWorker' };
    }

    if (!vapidPublicKey) {
      return { supported: false, reason: 'missingKey' };
    }

    return { supported: true };
  } catch (error) {
    return { supported: false, reason: 'unknown' };
  }
};

export const isPushSupported = () => getPushSupportStatus().supported;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * register() sonrası SW hemen active olmayabilir (özellikle iOS/Safari).
 * ready + active olana kadar bekler.
 */
export const waitForActiveServiceWorker = async (registration, timeoutMs = 10000) => {
  if (!registration) {
    throw new Error('Service Worker registration yok');
  }

  const ready = await navigator.serviceWorker.ready;
  const reg = ready || registration;
  if (reg.active) return reg;

  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (reg.active) return reg;
    if (reg.installing) {
      await new Promise((resolve) => {
        const worker = reg.installing;
        if (!worker || worker.state === 'activated') {
          resolve();
          return;
        }
        const onState = () => {
          if (worker.state === 'activated' || worker.state === 'redundant') {
            worker.removeEventListener('statechange', onState);
            resolve();
          }
        };
        worker.addEventListener('statechange', onState);
        setTimeout(() => {
          worker.removeEventListener('statechange', onState);
          resolve();
        }, 2000);
      });
      continue;
    }
    if (reg.waiting) {
      try {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      } catch (e) {
        // ignore
      }
    }
    await sleep(150);
  }

  if (!reg.active) {
    throw new Error('Service Worker aktif olmadı. Sayfayı yenileyip tekrar deneyin.');
  }
  return reg;
};

export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker desteklenmiyor');
  }

  try {
    const registration = await navigator.serviceWorker.register('/notification-sw.js', {
      scope: '/'
    });
    return waitForActiveServiceWorker(registration);
  } catch (error) {
    throw new Error(`Service Worker kaydı başarısız: ${error.message}`);
  }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Mevcut aboneliği al; yoksa yenisini oluştur.
 * Çağrı öncesi registration.active olmalı.
 */
export const getOrCreatePushSubscription = async (registration) => {
  if (!vapidPublicKey) {
    throw new Error('VAPID public key tanımlı değil');
  }

  const activeReg = await waitForActiveServiceWorker(registration);

  if (!('PushManager' in window) || !activeReg?.pushManager) {
    throw new Error('Push Manager desteklenmiyor');
  }

  try {
    let subscription = await activeReg.pushManager.getSubscription();
    if (subscription) {
      return subscription;
    }

    subscription = await activeReg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    return subscription;
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Bildirim izni verilmedi');
    }
    throw new Error(`Push aboneliği başarısız: ${error.message}`);
  }
};

/** @deprecated getOrCreatePushSubscription kullanın */
export const subscribeToPush = getOrCreatePushSubscription;

export const hasPushSubscriptions = (pushSubscription) => {
  if (!pushSubscription) return false;
  if (Array.isArray(pushSubscription)) {
    return pushSubscription.some((sub) => sub && typeof sub.endpoint === 'string' && sub.endpoint.length > 0);
  }
  return typeof pushSubscription.endpoint === 'string' && pushSubscription.endpoint.length > 0;
};

/**
 * Aynı anda tek push işlemi. İkinci çağrı mevcut promise'i paylaşır.
 */
export const runExclusivePushOperation = async (fn) => {
  if (pushInProgress) {
    return pushInProgress;
  }
  pushInProgress = (async () => {
    try {
      return await fn();
    } finally {
      pushInProgress = null;
    }
  })();
  return pushInProgress;
};

export { isIOS, isAndroid, isChrome };
